import { Spine } from "@esotericsoftware/spine-pixi-v7";
import * as PIXI from "pixi.js";
import TickerTimeout from "../../utils/TickerTimeout";
import store from "../../State/Store/store";
import {
  setFeatureSpinComplete,
  setFeatureSpinActive,
  rgsPlay,
  setPotCollectionScreenProgress,
  featureSpinManualStop,
  applyPotCollectionPendingRespins,
  incrementPotCollectionCoinCount,
  clearPotCollectionNewCoins,
  setShowPotCollectionOutro,
  setShowPotCollectionGrandJackpot,
  setShowPotCollectionMaxWin,
  openConnectionPopup,
  closeConnectionPopup,
  setReelsFrozen,
} from "../../State/actions";
import { Settings } from "../../settings";
import { FEATURES_CONFIG } from "../../featuresConfig";
import { getSymbolZIndex } from "../../SymbolData";
import { soundManager } from "../../SoundManager";
import { SOUND_NAME } from "../../sounds";

import PotCollectionScreenProgressProcessor from "./PotCollectionScreenProgressProcessor";
import PotCollectionOrbActivator from "./PotCollectionOrbActivator";
import PotCollectionEndOfBonusEngine from "./PotCollectionEndOfBonusEngine";

// ─── Constants ──────────────────────────────────────────────────────────────

const BLANK_SYMBOL_ID = 16;
const TOTAL_CELLS = Settings.REEL_COUNT * Settings.SYMBOLS_VISIBLE; // 15
const GRAND_JACKPOT_VALUE = 5000;     // ×Bet multiplier (GDD §14)

// Connection timeout: show "slow connection" popup if API hasn't responded
// after the expected spin duration + this buffer. Matches base game pattern.
const CONNECTION_TIMEOUT_BUFFER = 350; // ms (same as NATURAL_SPIN_DELAY in base game)

// Map feature letter to screen_progress action prefixes (same as PotCollectionControlContext)
const FEATURE_TO_ACTIONS = {
  M: ["m_reveals"],
  D: ["d_doubles", "d_reveals"],
  C: ["c_collect"],
};

// ── Zeus visual remap: post-feature IDs → raw orb IDs ──
// outcome.screen has post-feature IDs (21/22/23 for mystery, 20 for double).
// The static texture and landing animation must show raw orbs — the activation
// flow (OrbActivator) handles the visual transformation later.
function _remapLandingId(symbolId) {
  // All jackpot IDs (both base 17-19 and mystery-variant 21-23) must land as
  // raw mystery orb (13) — jackpots only appear via Mystery Zeus transformation.
  if (symbolId === 17 || symbolId === 18 || symbolId === 19 ||
      symbolId === 21 || symbolId === 22 || symbolId === 23) return 13;
  if (symbolId === 20) return 12; // Double variant → raw green orb
  return symbolId;
}

/**
 * PotCollectionSpinEngine
 *
 * Manages the cell-based spin loop for the Pot Collection bonus game.
 * Mirrors the Royal Collect spin engine pattern:
 *   1. Find blank cells (non-sticky positions)
 *   2. Hide overlay → show static → start spin
 *   3. Send `respin` to BE
 *   4. On response: set landing symbols → trigger cascading stops
 *   5. On all stopped: process screen_progress
 *   6. Check respins → schedule next or end bonus
 *
 * GDD: §13.1 steps 7–17, §8.2 speed modes
 *
 * Usage:
 *   const engine = new PotCollectionSpinEngine({ refs, dispatch, ... });
 *   engine.handleSpin(); // Called from event-driven spin trigger
 */
class PotCollectionSpinEngine {
  /**
   * @param {Object} params
   * @param {Object} params.reelRefs - PC reel refs
   * @param {Object} params.overlayReelRefs - PC overlay reel refs
   * @param {Object} params.collectionLayerRef - Collection layer ref
   * @param {Function} params.dispatch - Redux dispatch
   * @param {Function} params.onSpinComplete - Called when all stops + screen_progress done
   * @param {Function} params.onBonusEnd - Called when respins exhausted or grid full
   * @param {Object} params.gameViewControllerRef - GVC ref for GJP anticipation
   * @param {number} params.symbolWidth
   * @param {number} params.symbolHeight
   * @param {number} params.reelSpacing
   */
  constructor({
    reelRefs,
    overlayReelRefs,
    collectionLayerRef,
    respinCounterRef,
    dispatch,
    onSpinComplete,
    onBonusEnd,
    gameViewControllerRef,
    potChargingAnimatorRef,
    symbolWidth,
    symbolHeight,
    reelSpacing,
  }) {
    this.reelRefs = reelRefs;
    this.gameViewControllerRef = gameViewControllerRef;
    this.potChargingAnimatorRef = potChargingAnimatorRef;
    this._gjpAnticipationActive = false;
    this.overlayReelRefs = overlayReelRefs;
    this.collectionLayerRef = collectionLayerRef;
    this.respinCounterRef = respinCounterRef;
    this.dispatch = dispatch;
    this.onSpinComplete = onSpinComplete;
    this.onBonusEnd = onBonusEnd;
    this.symbolWidth = symbolWidth;
    this.symbolHeight = symbolHeight;
    this.reelSpacing = reelSpacing;

    this._isSpinning = false;
    this._spinningCount = 0;
    this._completedCount = 0;
    this._lastSpinTime = 0;
    this._stopTimeouts = new Map();
    this._autoStopTimeout = null;
    this._screenProgressProcessor = null;
    this._cancelled = false;
    this._windUpsComplete = false;
    this._manualStopPending = false;
    this._manualStopUnsubscribe = null;

    this._currentSpinBlankCells = null; // Cached at handleSpin — used for ALL stop paths
    this._activatedZeusPositions = new Set(); // Tracks ALL zeus positions activated across respins
    this._coinLandedThisSpin = false; // Reset per spin — first coin landing applies pending respins

    // ── Slam Stop / Speed Mode ──
    this._slamStopRequested = false;  // Reactive flag — animations check this at each await point
    this._activeOrbActivator = null;  // Current OrbActivator (for forwarding slamStop)
    this._featureAnimating = false;   // True between allCellsStopped and spin complete (feature execution window)

    // ── Connection Timeout (slow network safety) ──
    this._connectionTimeout = null;       // TickerTimeout for connection popup
    this._apiWaitUnsubscribe = null;      // Active store subscription from _waitForApiAndStop

    // ── Last-Coin Tension ──
    this._lastCoinTensionActive = false;  // True when tension is playing on the last blank cell
    this._tensionCell = null;             // { reelIndex, symbolIndex, symbolRef } — the cell under tension
    this._tensionStopTimeout = null;      // TickerTimeout for deferred tension stop
    this._pendingTensionCleanup = false;  // True when atmosphere cleanup is deferred until symbol lands
    this._tensionFiredThisSpin = false;   // True once tension is computed for this spin — prevents re-triggering

    // ── Cascade Stop Guard ──
    this._cascadeStopsTriggered = false;  // True after first _triggerCascadingStops call — prevents duplicate calls

    // ── Reel Glow ──
    this._reelGlowPlayed = false;         // True once reel glow fires on grid full (prevents duplicate)
    this._pendingGjpGlow = false;         // True when 15th coin detected — glow fires after its 'in' animation
  }

  // ── Blank Symbol Discovery ──────────────────────────────────────────────────

  /**
   * Find all blank (spinnable) cells in the current grid.
   * Returns array of { reelIndex, symbolIndex, symbolRef, overlaySymbolRef }.
   */
  _getBlankCells() {
    const blanks = [];
    const state = store.getState();

    // Determine the correct grid source:
    // - After a respin API response (phase="active"), currentRound.reels has the updated bonus grid
    // - On the FIRST spin, currentRound.reels still has the base game trigger round (no blanks!)
    //   so we fall back to initialRespinScreen which is the actual bonus starting grid
    const roundReels = state.potCollection?.currentRound?.reels;
    const initialReels = state.potCollection?.initialRespinScreen;

    const roundHasBlanks = roundReels?.some(reel =>
      Array.isArray(reel) && reel.some(id => parseInt(id, 10) === BLANK_SYMBOL_ID)
    );

    const currentReels = roundHasBlanks ? roundReels : initialReels;

    if (!currentReels || !currentReels.length) {
      return blanks;
    }

    for (let ri = 0; ri < Settings.REEL_COUNT; ri++) {
      const reel = currentReels[ri];
      if (!Array.isArray(reel)) {
        continue;
      }

      for (let si = 0; si < Settings.SYMBOLS_VISIBLE; si++) {
        const symbolId = parseInt(reel[si], 10);
        if (symbolId === BLANK_SYMBOL_ID) {
          const reelRef = this.reelRefs?.current?.[ri];
          const symbolRef = reelRef?.getSymbolRef?.(si);
          const overlayReelRef = this.overlayReelRefs?.current?.[ri];
          const overlaySymbolRef = overlayReelRef?.getSymbolRef?.(si);

          blanks.push({
            reelIndex: ri,
            symbolIndex: si,
            symbolRef,
            overlaySymbolRef,
          });
        }
      }
    }

    return blanks;
  }

  /**
   * Start a respin. Rate-limited, resets all spin state.
   * Uses wind-up cascade for AUTO speed (mirrors RC context pattern).
   */
  handleSpin() {
    if (this._isSpinning) {
      return;
    }

    // Block any spin after end-of-bonus collection has started
    if (this._endOfBonusActive) {

      return;
    }

    // ── No respins left means no request, whoever asked ──
    //
    // The two flags above leave a wide window open. _handleAllCellsStopped is
    // async and clears _isSpinning on its FIRST line, but only decides the
    // bonus is over hundreds of milliseconds later, after the feature
    // animations — and _endOfBonusActive is set at the very end of that. For
    // that whole span both guards read false while the round may already be
    // finished on the server.
    //
    // Callers were each expected to check respinsLeft themselves. Most did;
    // the turbo shortcut in PotCollectionControlContext did not. Checking here
    // covers every caller, including ones added later, and this is the last
    // point before dispatch(rgsPlay()).
    const respinsLeft = store.getState().potCollection?.respinsLeft ?? 0;
    if (respinsLeft <= 0) {
      return;
    }

    const now = Date.now();
    this._lastSpinTime = now;

    // Clear pending timeouts
    this._clearTimeouts();

    this._isSpinning = true;
    this._completedCount = 0;
    this._manualStopPending = false;
    this._windUpsComplete = false;
    this._coinLandedThisSpin = false;
    this._slamStopRequested = false;
    this._featureAnimating = false;
    this._tensionFiredThisSpin = false;
    this._cascadeStopsTriggered = false;

    // Reset tension state from previous spin
    this._cancelTension();

    // Clean up any lingering manual stop subscription from previous spin
    if (this._manualStopUnsubscribe) {
      this._manualStopUnsubscribe();
      this._manualStopUnsubscribe = null;
    }

    this.dispatch(setFeatureSpinComplete({ complete: false, bet: store.getState().game.bet }));

    // Clear stale newCoins from previous spin — defense in depth for _computeTensionCell.
    // Without this, newCoins persists until the API responds for the current spin,
    // which can cause false anticipation if coinCount + stale newCoins.length >= 14.
    this.dispatch(clearPotCollectionNewCoins());

    const blankCells = this._getBlankCells();
    this._currentSpinBlankCells = blankCells; // Cache for manual stop / auto stop paths
    this._spinningCount = blankCells.length;

    if (blankCells.length === 0) {
      this._isSpinning = false;
      this.dispatch(setFeatureSpinComplete({ complete: true, bet: store.getState().game.bet }));
      this.onBonusEnd?.("grid_full");
      return;
    }

    // Bonus always runs at AUTO speed
    const config = FEATURES_CONFIG.POT_COLLECTION;
    const speedConfig = config.SPIN_SPEED;

    // Force-reset internal state of all symbol controllers
    // (mirrors RC context pattern: clear stale data from previous spin)

    blankCells.forEach(({ symbolRef, reelIndex, symbolIndex }) => {
      const controller = symbolRef?.current?.getSymbolController?.();
      if (controller) {
        controller.isStopping = false;
        controller.hasStopped = false;
        controller.pendingLandingSymbolId = null;
        controller.autoPlaySpeed = "auto";
        controller._speedConfigCache = null;

      } else {

      }
    });

    // Hide overlay animations for blank cells + prepare for spin
    blankCells.forEach(({ overlaySymbolRef, symbolRef }) => {
      if (overlaySymbolRef?.hideAnimation) {
        overlaySymbolRef.hideAnimation();
      }
      // showSymbolTexture is handled inside startSpin/startWindUp
      // (the static sprite is shown before controller hides it and shows spinning sprites)
      if (symbolRef?.current?.showSymbolTexture) {
        symbolRef.current.showSymbolTexture();
      }
    });

    // Set onStopCallback on each controller for this spin

    blankCells.forEach(({ symbolRef, reelIndex, symbolIndex }) => {
      const controller = symbolRef?.current?.getSymbolController?.();
      if (controller) {
        controller.onStopCallback = (si, ri) => {
          this._handleSymbolStopComplete(ri, si);
        };

      }
    });

    // Dispatch spin to BE
    this.dispatch(setFeatureSpinActive(true));
    this.dispatch(rgsPlay());

    // ── Fast-path failure detection ──
    // Subscribe immediately to catch API rejections BEFORE any wind-up or
    // auto-stop timeout fires. Ensures cells freeze instantly on error.
    const failFastUnsubscribe = store.subscribe(() => {
      const s = store.getState();
      if (s.spin.featureSpinStatus === "failed") {
        failFastUnsubscribe();
        this._clearTimeouts();       // Cancel wind-up cascade + auto-stop
        this._clearConnectionTimeout();
        this._freezeAllSpinningCells();
      } else if (s.spin.featureSpinStatus === "succeeded") {
        // Normal path — wind-up/auto-stop timeouts handle it
        failFastUnsubscribe();
      }
    });
    // Store reference so cancel() can clean up
    this._failFastUnsubscribe = failFastUnsubscribe;

    // ── Connection timeout (slow network safety — ported from ReelControlContext) ──
    // TickerTimeout so tab-hide freezes it with the rest of the spin pipeline.
    if (this._connectionTimeout) {
      this._connectionTimeout.clear();
    }
    const expectedDuration = speedConfig.SPIN_DURATION || 1500;
    this._connectionTimeout = new TickerTimeout(() => {
      this._connectionTimeout = null;
      const uiState = store.getState();
      if (!uiState.userInterface.connectionPopupActive) {
        this.dispatch(openConnectionPopup());
      }
    }, expectedDuration + CONNECTION_TIMEOUT_BUFFER);

    // Decrement counter now (cells start spinning immediately)
    const counterCtrl = this.respinCounterRef?.current;
    if (counterCtrl) {
      const displayed = counterCtrl.displayedValue ?? 3;
      const nextVal = Math.max(0, displayed - 1);
      counterCtrl.playAnimation(nextVal);
    }

    // Play spin sound
    soundManager.play(SOUND_NAME.feature_reel_spin);

    // ── Wind-up cascade ──────────────────────────────────────────────────
    // ── Sole blank cell: skip wind-up, start spinning then wait for API ──
    // When only 1 blank cell remains, the player already knows it's the
    // last coin. No wind-up needed — go straight into spin.
    // IMPORTANT: Tension does NOT start until API responds (coin count must
    // be verified). The user confirmed: tension cannot start before API.
    const isSoleTensionCell = blankCells.length === 1 &&
      FEATURES_CONFIG.POT_COLLECTION.SYMBOL_TENSION?.ENABLED;

    if (isSoleTensionCell) {
      const cell = blankCells[0];

      // Start spin directly (no wind-up animation)
      cell.symbolRef.current?.startSpin();
      this._windUpsComplete = true;

      // Pre-cache tension cell for faster startup — but do NOT mark
      // _lastCoinTensionActive here. Actual activation is deferred to
      // _handleApiSuccess where _computeTensionCell verifies the real
      // coin count from the API response. This prevents false anticipation
      // when 1 blank remains but coin count < 14.
      this._preCachedTensionCell = cell;

      // Wait for API response to set landing data AND decide on tension
      this._waitForApiAndStop(blankCells, false);
    } else {
      // Cascading wind-up → spin → deferred auto-stop
      const cascadeDelay = speedConfig.WIND_UP_CASCADE_DELAY || 0;

      // Callback-based wind-up completion — fires the instant the last cell finishes
      let windUpCompletedCount = 0;
      const totalWindUps = blankCells.length;

      const onWindUpDone = () => {
        windUpCompletedCount++;
        if (windUpCompletedCount < totalWindUps) return;

        // All wind-ups complete — proceed immediately
        this._windUpsComplete = true;

        // Check for immediate failure before any spin path
        const immediateState = store.getState();
        if (immediateState.spin.featureSpinStatus === "failed") {
          this._clearConnectionTimeout();
          this._freezeAllSpinningCells();
          return;
        }

        // If manual stop was pressed during wind-up, execute it now
        if (this._manualStopPending) {
          const state = store.getState();
          if (state.spin.featureSpinStatus === "succeeded") {
            this._triggerCascadingStops(blankCells, true);
          } else {
            // Data not yet — subscribe
            const unsubscribe = store.subscribe(() => {
              const currentState = store.getState();
              if (currentState.spin.featureSpinStatus === "succeeded") {
                this._triggerCascadingStops(blankCells, true);
                if (this._manualStopUnsubscribe) {
                  this._manualStopUnsubscribe();
                  this._manualStopUnsubscribe = null;
                }
              } else if (currentState.spin.featureSpinStatus === "failed") {

                if (this._manualStopUnsubscribe) {
                  this._manualStopUnsubscribe();
                  this._manualStopUnsubscribe = null;
                }
                this._clearConnectionTimeout();
                this._freezeAllSpinningCells();
              }
            });
            this._manualStopUnsubscribe = unsubscribe;
          }
          return;
        }

        // No manual stop pending — start auto-stop timeout
        this._autoStopTimeout = new TickerTimeout(() => {
          this._waitForApiAndStop(blankCells, false);
        }, speedConfig.SPIN_DURATION);
      };

      // Start all wind-ups with callback — tight for loop for zero-stutter simultaneous start
      for (let i = 0; i < blankCells.length; i++) {
        const { symbolRef } = blankCells[i];
        if (cascadeDelay === 0) {
          symbolRef.current?.startWindUp(onWindUpDone);
        } else {
          const delay = i * cascadeDelay;
          if (delay === 0) {
            symbolRef.current?.startWindUp(onWindUpDone);
          } else {
            const tid = new TickerTimeout(() => {
              symbolRef.current?.startWindUp(onWindUpDone);
            }, delay);
            this._stopTimeouts.set(`windup-${i}`, tid);
          }
        }
      }
    }
  }

  // ── Manual Stop ─────────────────────────────────────────────────────────────

  handleManualStop() {
    // Guard: nothing to stop if not spinning (defense against turbo toggle race)
    if (!this._isSpinning) {
      return;
    }

    // If tension animation is actively playing (not just pre-computed),
    // second press stops it immediately
    if (this._lastCoinTensionActive && this._tensionCell && this._tensionStopTimeout) {
      this._stopSymbolTension();
      return;
    }

    // If tension was just stopped (_stopSymbolTension set _pendingTensionCleanup)
    // but the cell hasn't finished its BackOut bounce yet, don't re-enter the
    // normal stop path. Re-entering would recompute tension → restart GVC shake
    // on a cell that's already mid-stop → the new shake never gets cleaned up.
    // (Root cause of stuck-shake bug on double-click.)
    if (this._pendingTensionCleanup) {
      return;
    }

    // ── Override in-progress auto-stop cascade ──
    // If auto-stop already fired _triggerCascadingStops, cells are stopping one-by-one
    // with SYMBOL_STOP_CASCADE_DELAY (e.g. 200ms). Player pressing stop should cancel
    // those pending timeouts and re-run the cascade with manual stop parameters
    // (0ms cascade delay, faster landing) for all cells not yet stopped.
    if (this._cascadeStopsTriggered) {
      // Cancel all pending cascade stop timeouts (stop-*, tension-start)
      for (const [key, timeout] of this._stopTimeouts) {
        if (key.startsWith('stop-') || key === 'tension-start') {
          timeout.clear();
          this._stopTimeouts.delete(key);
        }
      }

      // Skip cancelled a pending anticipation start — drop the plan so the
      // re-cascade does NOT reschedule tension-start (QA: skip with 3 blanks
      // incorrectly started anticipation on the last cell of the last reel).
      if (this._lastCoinTensionActive && this._tensionCell && !this._tensionStopTimeout) {
        this._lastCoinTensionActive = false;
        this._tensionCell = null;
      }

      // Reset guard so _triggerCascadingStops can re-enter.
      // Cells already told to stop (isStopping=true) will be skipped by stopSpin's guard —
      // they'll finish their auto-stop easing naturally. Cells whose timeout was cancelled
      // will get stopSpin(true) with 0ms cascade delay.
      this._cascadeStopsTriggered = false;

      const blankCells = this._currentSpinBlankCells || [];
      this.dispatch(featureSpinManualStop());
      this._triggerCascadingStops(blankCells, true);
      return;
    }

    // Tension already decided for this spin but cascade hasn't started yet
    // (shouldn't happen in the common path). Force a manual cascade instead of
    // bailing — a bare return left cells spinning forever.
    if (this._tensionFiredThisSpin) {
      const blankCells = this._currentSpinBlankCells || [];
      this.dispatch(featureSpinManualStop());
      this._triggerCascadingStops(blankCells, true);
      return;
    }

    this._manualStopPending = true;

    // NOTE: Do NOT stop tension here — manual stop only stops non-tension cells.
    // Tension proceeds naturally (matching base game behavior).
    // _triggerCascadingStops already skips the tension cell and schedules
    // tension start after the last non-tension cell lands.

    // If wind-ups are still in progress, defer stop until cascade completes.
    if (!this._windUpsComplete) {
      this.dispatch(featureSpinManualStop());
      return;
    }

    // Clear the auto-stop timeout — we're stopping now
    if (this._autoStopTimeout) {
      this._autoStopTimeout.clear();
      this._autoStopTimeout = null;
    }

    // Cancel pending auto-stop API subscription — manual stop takes over.
    // Without this, both manual and auto subscriptions fire on API success → duplicate cascadingStops.
    if (this._apiWaitUnsubscribe) {
      this._apiWaitUnsubscribe();
      this._apiWaitUnsubscribe = null;
    }

    this.dispatch(featureSpinManualStop());

    // Use cached blank cells from handleSpin — NOT _getBlankCells().
    // After API responds, Redux grid is updated with post-spin data (new coins),
    // so _getBlankCells() would miss cells that were blank but now have coins.
    // Those cells are still physically spinning and need to be stopped.
    const blankCells = this._currentSpinBlankCells || this._getBlankCells();

    // Check if API data already arrived
    const state = store.getState();
    if (state.spin.featureSpinStatus === "succeeded") {
      this._triggerCascadingStops(blankCells, true);
      return;
    }

    // Check if API already failed (FR cancellation can reject before manual stop)
    if (state.spin.featureSpinStatus === "failed") {
      this._clearConnectionTimeout();
      this._freezeAllSpinningCells();
      return;
    }

    // Data not yet arrived — subscribe and stop when it does
    const unsubscribe = store.subscribe(() => {
      const currentState = store.getState();
      if (currentState.spin.featureSpinStatus === "succeeded") {
        this._triggerCascadingStops(blankCells, true);
        if (this._manualStopUnsubscribe) {
          this._manualStopUnsubscribe();
          this._manualStopUnsubscribe = null;
        }
      } else if (currentState.spin.featureSpinStatus === "failed") {

        if (this._manualStopUnsubscribe) {
          this._manualStopUnsubscribe();
          this._manualStopUnsubscribe = null;
        }
        this._clearConnectionTimeout();
        this._freezeAllSpinningCells();
      }
    });
    this._manualStopUnsubscribe = unsubscribe;
  }

  // ── Slam Stop (Feature Animations) ────────────────────────────────────────

  /**
   * Request slam stop for feature animations.
   * Called by the button when spacebar is pressed during feature animation window.
   * Running animations check this._slamStopRequested at each await point and fast-forward.
   */
  requestSlamStop() {
    if (this._slamStopRequested) return; // Already requested, idempotent
    this._slamStopRequested = true;
    // Forward to active OrbActivator so it skips activation delays/bolts
    if (this._activeOrbActivator) {
      this._activeOrbActivator.requestSlamStop();
    }
    // Forward to EOB engine if it exists — turbo toggle may fire before
    // _collectionActive is set, so always forward when the engine is alive.
    if (this._endOfBonusEngine) {
      this._endOfBonusEngine.requestSlamStop();
    }
  }

  /**
   * Reset slam stop after the current feature completes.
   * Each feature must be slammed individually — slam stop is per-feature.
   */
  _resetSlamStopForNextFeature() {
    if (this._slamStopRequested) {
      this._slamStopRequested = false;
      if (this._activeOrbActivator) {
        this._activeOrbActivator._slamStopRequested = false;
      }
    }
  }

  // ── API Response Wait + Stop Trigger ────────────────────────────────────────

  _waitForApiAndStop(blankCells, isManualStop = false) {
    // Cancel any existing API wait subscription (prevents duplicate subscriptions
    // when manual stop + auto-stop both call this — root cause of overlapping animations)
    if (this._apiWaitUnsubscribe) {
      this._apiWaitUnsubscribe();
      this._apiWaitUnsubscribe = null;
    }

    // Check if API data already arrived (turbo mode race condition)
    const currentState = store.getState();
    if (currentState.spin.featureSpinStatus === "succeeded") {
      this._clearConnectionTimeout();
      this._handleApiSuccess(blankCells, isManualStop);
      return;
    }

    // Check if API already failed (FR cancellation can reject before this is called)
    if (currentState.spin.featureSpinStatus === "failed") {
      this._clearConnectionTimeout();
      this._freezeAllSpinningCells();
      return;
    }

    // Subscribe to store — wait for featureSpinStatus === "succeeded"
    // Subscribe to store — wait for featureSpinStatus === "succeeded"
    const unsubscribe = store.subscribe(() => {
      if (this._cancelled) {
        unsubscribe();
        this._apiWaitUnsubscribe = null;
        return;
      }

      const state = store.getState();
      const spinStatus = state.spin.featureSpinStatus;

      if (spinStatus === "succeeded") {
        unsubscribe();
        this._apiWaitUnsubscribe = null;
        this._clearConnectionTimeout();
        this._handleApiSuccess(blankCells, isManualStop);
      } else if (spinStatus === "failed") {

        unsubscribe();
        this._apiWaitUnsubscribe = null;
        this._clearConnectionTimeout();
        this._freezeAllSpinningCells();
      }
    });
    this._apiWaitUnsubscribe = unsubscribe;
  }

  _handleApiSuccess(blankCells, isManualStop) {
    // Counter decrement already happened at handleSpin() time for AUTO mode
    // (cells start spinning immediately so counter must tick down right away).

    // Dismiss connection popup if it was shown
    this._dismissConnectionPopup();

    const state = store.getState();
    const pcState = state.potCollection;
    const screenProgress = pcState.screenProgress;

    // Set screen progress for processing
    if (screenProgress && screenProgress.length > 0) {
      this.dispatch(setPotCollectionScreenProgress(screenProgress));
    }

    // ── Pre-calculate last-coin tension ──
    // Tension ONLY when exactly 1 blank remains (_computeTensionCell enforces
    // that). Must run after API responds so coinCount is verified (>= 14).
    if (!this._lastCoinTensionActive && !this._tensionFiredThisSpin) {
      const tensionConfig = FEATURES_CONFIG.POT_COLLECTION.SYMBOL_TENSION;
      if (tensionConfig?.ENABLED) {
        const tensionCell = this._computeTensionCell(blankCells);
        // Mark as computed UNCONDITIONALLY — prevents duplicate _triggerCascadingStops
        // calls from recomputing with a different (incremented) coinCount.
        // Without this, fast spacebar presses can cause a second _computeTensionCell
        // call after cells have landed and coinCount has been incremented, leading
        // to false positives (anticipation at 13 coins bug).
        this._tensionFiredThisSpin = true;
        if (tensionCell) {
          this._lastCoinTensionActive = true;
          this._tensionCell = tensionCell;

          // Sole-blank path: pre-cached cell is the only cell — start tension immediately
          if (this._preCachedTensionCell) {
            this._startSymbolTension(this._tensionCell);
            this._preCachedTensionCell = null;
          }
        } else {
          // _computeTensionCell returned null — not enough coins for anticipation.
          // Clear any pre-cached cell from the sole-blank path.
          this._preCachedTensionCell = null;
        }
      }
    }

    // Trigger cascading stops
    this._triggerCascadingStops(blankCells, isManualStop);
  }

  // ── Cascading Stops ─────────────────────────────────────────────────────────

  /**
   * Set landing symbols on controllers and stop cells left→right with staggered delays.
   * Uses the controller's ticker-based BackOut easing stop (no anime.js).
   */
  _triggerCascadingStops(blankCells, isManualStop = false) {
    // Guard: prevent duplicate cascade stop calls within the same spin.
    // Fast spacebar presses can cause multiple code paths (windUpCheck subscription,
    // handleManualStop direct call) to invoke this method. The second call would see
    // a higher coinCount (cells from the first call already landed and incremented it),
    // causing _computeTensionCell to produce false positives.
    if (this._cascadeStopsTriggered) {
      return;
    }
    this._cascadeStopsTriggered = true;

    // Compute tension if not already set — manual stop bypasses _handleApiSuccess
    // where tension is normally computed. Covers all manual stop call sites.
    // _tensionFiredThisSpin: once tension has been computed for this spin,
    // never recompute — prevents click spam from re-triggering tension
    // on a cell that already landed.
    if (!this._lastCoinTensionActive && !this._tensionFiredThisSpin) {
      const tensionConfig = FEATURES_CONFIG.POT_COLLECTION.SYMBOL_TENSION;
      if (tensionConfig?.ENABLED) {
        const tensionCell = this._computeTensionCell(blankCells);
        // Mark as computed UNCONDITIONALLY (same rationale as _handleApiSuccess).
        this._tensionFiredThisSpin = true;
        if (tensionCell) {
          this._lastCoinTensionActive = true;
          this._tensionCell = tensionCell;
        }
      }
    }

    // Manual skip before anticipation visuals start: do not defer a tension cell.
    // Stop every blank immediately — prevents skip from starting anticipation
    // on the last cascade cell (usually last reel / bottom row).
    if (
      isManualStop &&
      this._lastCoinTensionActive &&
      this._tensionCell &&
      !this._tensionStopTimeout
    ) {
      const pendingStart = this._stopTimeouts.get("tension-start");
      if (pendingStart) {
        pendingStart.clear();
        this._stopTimeouts.delete("tension-start");
      }
      this._lastCoinTensionActive = false;
      this._tensionCell = null;
    }

    const config = FEATURES_CONFIG.POT_COLLECTION;
    const speedConfig = config.SPIN_SPEED;

    // Sort by reel index then symbol index (left→right, top→bottom)
    const sorted = [...blankCells].sort((a, b) =>
      a.reelIndex !== b.reelIndex
        ? a.reelIndex - b.reelIndex
        : a.symbolIndex - b.symbolIndex
    );

    // Get new screen from API response for landing symbols
    const state = store.getState();
    const currentReels = state.potCollection?.currentRound?.reels;

    const baseDelay = isManualStop ? 0 : (speedConfig.SYMBOL_STOP_BASE_DELAY || 0);
    const cascadeDelay = isManualStop
      ? (speedConfig.SYMBOL_STOP_CASCADE_DELAY_MANUAL || 0)
      : (speedConfig.SYMBOL_STOP_CASCADE_DELAY || 150);

    // Track the last non-tension cell's delay for tension chaining
    let lastNonTensionDelay = 0;

    sorted.forEach(({ symbolRef, reelIndex, symbolIndex }, i) => {
      const landingSymbolId = currentReels?.[reelIndex]?.[symbolIndex];
      const parsedId = _remapLandingId(landingSymbolId !== undefined ? parseInt(landingSymbolId, 10) : BLANK_SYMBOL_ID);

      // Set landing symbol on the controller (API data, already remapped)
      if (symbolRef?.current?.setLandingSymbolId) {
        symbolRef.current.setLandingSymbolId(parsedId);
      }

      // ── Skip the tension cell — its stop is deferred ──
      const isTensionCell = this._lastCoinTensionActive &&
        this._tensionCell &&
        reelIndex === this._tensionCell.reelIndex &&
        symbolIndex === this._tensionCell.symbolIndex;

      if (isTensionCell) {
        return;
      }

      const delay = baseDelay + i * cascadeDelay;
      lastNonTensionDelay = delay;

      const stopFn = () => {
        if (this._cancelled) return;
        if (symbolRef?.current?.stopSpin) {
          symbolRef.current.stopSpin(isManualStop);
        }
      };

      if (delay === 0) {
        stopFn();
      } else {
        const tid = new TickerTimeout(stopFn, delay);
        this._stopTimeouts.set(`stop-${reelIndex}-${symbolIndex}`, tid);
      }
    });

    // ── Start tension on the deferred cell after other cells land ──
    // Skip if tension is already running (sole-blank immediate start)
    if (this._lastCoinTensionActive && this._tensionCell && !this._tensionStopTimeout) {
      const stConfig = FEATURES_CONFIG.POT_COLLECTION.SYMBOL_TENSION;
      const gapDelay = stConfig?.TENSION_START_DELAY ?? 350;
      const tensionDelay = lastNonTensionDelay + gapDelay;
      const tid = new TickerTimeout(() => {
        if (this._cancelled || !this._lastCoinTensionActive) return;
        this._startSymbolTension(this._tensionCell);
      }, tensionDelay);
      this._stopTimeouts.set('tension-start', tid);
    }
  }

  // ── Symbol Stop Complete ────────────────────────────────────────────────────

  /**
   * Called by the controller's onStopCallback when a symbol's BackOut easing
   * animation completes and the symbol has landed.
   */
  _handleSymbolStopComplete(reelIndex, symbolIndex) {
    this._completedCount++;

    // Determine landed symbol FIRST so we know if this is the 15th coin
    // before deciding whether to skip destroying the GJP anticipation overlay.
    const state = store.getState();
    const currentReels = state.potCollection?.currentRound?.reels;
    const landedSymbolId = currentReels?.[reelIndex]?.[symbolIndex];
    const parsedId = landedSymbolId !== undefined ? parseInt(landedSymbolId, 10) : BLANK_SYMBOL_ID;

    // Deferred tension atmosphere cleanup — symbol has now fully landed.
    // If this is the 15th coin (willBeGridFull), skip destroying the GJP anticipation
    // overlay — we transition it into win state instead.
    // Exception: when winLimitReached, we DON'T preserve GJP visuals (no GJP popup will show).
    if (this._pendingTensionCleanup) {
      const coinCountNow = (store.getState().potCollection?.coinCount || 0);
      const afterIncrement = parsedId !== BLANK_SYMBOL_ID ? coinCountNow + 1 : coinCountNow;
      const winLimitReached = !!store.getState().potCollection?.winLimitReached;
      const willBeGridFull = afterIncrement >= TOTAL_CELLS && !winLimitReached;
      this._cleanupTensionAtmosphere(willBeGridFull /* skipGjp */);
    }

    // Check if this position is a new coin (from RGS.js computed diff)
    const newCoins = state.potCollection?.newCoins || [];
    const matchingNewCoin = newCoins.find(c => c.reelIndex === reelIndex && c.symbolIndex === symbolIndex);
    // If a coin/jackpot/zeus landed, trigger landing animation via overlay
    if (parsedId !== BLANK_SYMBOL_ID) {
      // ── Coin counter: increment on every non-blank landing ──
      this.dispatch(incrementPotCollectionCoinCount());

      // ── Grid full: trigger win anticipation + reel glow ──
      // Skip when winLimitReached — no GJP popup will show, bonus ends with Max Win instead.
      const coinCountNow = (store.getState().potCollection?.coinCount || 0);
      const winLimitReached = !!store.getState().potCollection?.winLimitReached;
      if (coinCountNow >= TOTAL_CELLS && !this._reelGlowPlayed && !winLimitReached) {
        this._reelGlowPlayed = true;
        // Flag: glow + GJP win will fire AFTER the 15th coin's landing animation completes.
        // The actual trigger happens below, after showLandingAnimation sets up the overlay spine.
        this._pendingGjpGlow = true;
      }

      // ── Zeus visual fix: land as raw orb, not post-feature ID ──
      // outcome.screen shows post-feature IDs (21/22/23 for mystery, 20 for double).
      // But visually, new Zeus must land as raw orbs (13=mystery, 12=double) because
      // the OrbActivator activation flow handles the transformation (shuffle → reveal).
      // Only remap NEW Zeus coins — existing ones from prev spins stay as-is.
      let landingId = parsedId;
      if (matchingNewCoin?.isZeus) {
        if (matchingNewCoin.zeusFeature === "M") {
          landingId = 13; // Raw mystery orb (cm)
        } else if (matchingNewCoin.zeusFeature === "D" && parsedId === 20) {
          landingId = 12; // Raw double orb (cd)
        }
        // Collect (14) has no variant — no remap needed
      }

      // Update static texture to landed symbol (fallback + correct texture when overlay hides)
      // Hide it BEFORE showing overlay spine to prevent 1-frame flash (both visible simultaneously)
      const reelRef = this.reelRefs?.current?.[reelIndex];
      const staticRef = reelRef?.getSymbolRef?.(symbolIndex);
      if (staticRef?.current) {
        staticRef.current.updateSymbolTexture?.(landingId);
        staticRef.current.hideSymbolTexture?.();
      }

      // Now show overlay spine — static is already hidden, no flash
      const overlayReelRef = this.overlayReelRefs?.current?.[reelIndex];
      const overlaySymbolRef = overlayReelRef?.getSymbolRef?.(symbolIndex);
      if (overlaySymbolRef?.showLandingAnimation) {
        overlaySymbolRef.showLandingAnimation(landingId);
      }

      // ── GJP glow: fire AFTER 15th coin's landing animation completes ──
      // The 'in' animation must finish (coin visually lands) before the
      // reel glow + gjp_anticipation_win overlay fires.
      if (this._pendingGjpGlow) {
        this._pendingGjpGlow = false;
        const activeSpine = overlaySymbolRef?.getActiveSpine?.();
        if (activeSpine?.state) {
          this._reelGlowPromise = new Promise((resolve) => {
            const glowListener = {
              complete: (entry) => {
                // Fire on 'in' / 'land' / 'landing' completion (first non-looping animation)
                if (entry.loop) return;
                activeSpine.state.removeListener(glowListener);
                this._playGjpAnticipationWin();
                this._playReelGlow().then(resolve).catch(resolve);
              },
            };
            activeSpine.state.addListener(glowListener);

            // Safety: if 'in' animation is missing or instant, fire after a short delay
            const safetyTid = new TickerTimeout(() => {
              activeSpine.state.removeListener(glowListener);
              this._playGjpAnticipationWin();
              this._playReelGlow().then(resolve).catch(resolve);
            }, 2000);
            this._stopTimeouts.set('gjp-glow-safety', safetyTid);

            // Clear safety when listener fires
            const origComplete = glowListener.complete;
            glowListener.complete = (entry) => {
              safetyTid.clear();
              this._stopTimeouts.delete('gjp-glow-safety');
              origComplete(entry);
            };
          });
        } else {
          // No active spine — fallback: fire immediately
          this._reelGlowPromise = new Promise((resolve) => {
            this._playGjpAnticipationWin();
            this._playReelGlow().then(resolve).catch(resolve);
          });
        }
      }

      // ── Z-index priority (flat overlay: all symbols are siblings) ──
      // See getSymbolZIndex in SymbolData.js for full hierarchy.
      if (overlaySymbolRef?.setZIndex) {
        overlaySymbolRef.setZIndex(getSymbolZIndex(landingId));
      }

      // ── Respin counter: update on first coin landing ──
      // Read the API’s pending respins value and imperatively update the counter.
      // Skip visual update to 0 — end-of-bonus sequence handles it with out animation.
      if (!this._coinLandedThisSpin) {
        this._coinLandedThisSpin = true;
        const pendingVal = store.getState().potCollection?.pendingRespinsLeft;
        const counterCtrl = this.respinCounterRef?.current;
        if (pendingVal != null && pendingVal > 0 && counterCtrl) {
          counterCtrl.playAnimation(pendingVal);
        }
        // Sync Redux state
        this.dispatch(applyPotCollectionPendingRespins());
      }
    } else {
      // Blank symbol landed — show the static texture
      const reelRef = this.reelRefs?.current?.[reelIndex];
      const staticRef = reelRef?.getSymbolRef?.(symbolIndex);
      if (staticRef?.current) {
        staticRef.current.updateSymbolTexture?.(parsedId);
        staticRef.current.showSymbolTexture?.();
      }
    }

    // Check if all cells have stopped
    if (this._completedCount >= this._spinningCount) {
      soundManager.stop(SOUND_NAME.feature_reel_spin);

      // ── Bonus End animation (non-GJP only, OR max win with full grid) ──
      // Fire at the same moment as GJP glow — right when all cells officially stop.
      // Gated: respins exhausted AND (grid is NOT full OR it's a max win).
      // Max win with 15 coins skips GJP glow, so bonus_end fills that gap.
      const pendingRespins = store.getState().potCollection?.pendingRespinsLeft ?? store.getState().potCollection?.respinsLeft ?? 0;
      const currentCoinCount = store.getState().potCollection?.coinCount || 0;
      const winLimitReached = !!store.getState().potCollection?.winLimitReached;
      if (pendingRespins <= 0 && (currentCoinCount < TOTAL_CELLS || winLimitReached)) {
        this._playBonusEnd();
      }

      this._handleAllCellsStopped();
    }
  }

  // ── All Cells Stopped ───────────────────────────────────────────────────────

  async _handleAllCellsStopped() {
    this._isSpinning = false;

    const state = store.getState();
    const newCoins = state.potCollection?.newCoins || [];
    const bet = state.game.bet || 1;

    // ── Lazy-seed _activatedZeusPositions from initial bonus screen ──
    // OrbActivator uses skipPositions to avoid re-activating Zeus from the trigger spin.
    // Seed once from initialRespinScreen (ids 12=cd, 13=cm, 14=cc).
    if (this._activatedZeusPositions.size === 0) {
      const initialScreen = state.potCollection?.initialRespinScreen;
      if (initialScreen) {
        for (let ri = 0; ri < initialScreen.length; ri++) {
          if (!Array.isArray(initialScreen[ri])) continue;
          for (let si = 0; si < initialScreen[ri].length; si++) {
            const id = parseInt(initialScreen[ri][si], 10);
            if (id === 12 || id === 13 || id === 14) {
              this._activatedZeusPositions.add(`${ri}_${si}`);
            }
          }
        }
      }
    }

    // ── Filter screen_progress: skip new_coins during respins ──
    // _handleSymbolStopComplete already plays landing animations and the coin counter
    // is managed separately. Processing new_coins would double-increment coinCount
    // and replay landing animations.
    let screenProgress = (state.potCollection?.screenProgress || []).filter(a => a.action !== "new_coins");

    // ── Synthesize missing m_reveals actions ──
    // When mystery Zeus lands (id 21/22/23), the server resolves the jackpot tier
    // and encodes it in the symbol ID, but sometimes only includes new_coins
    // in screen_progress (no m_reveals). We synthesize the m_reveals from newCoins
    // so OrbActivator has actions to process for the reveal animation.
    const MYSTERY_ID_TO_TIER = { 21: "minor", 22: "mini", 23: "major" };
    const TIER_TO_VALUE = { mini: 15, minor: 50, major: 150 };
    const mysteryCoins = newCoins.filter(c => c.zeusFeature === "M" && MYSTERY_ID_TO_TIER[c.symbolId]);
    const existingMReveals = screenProgress.filter(a => a.action === "m_reveals");

    if (mysteryCoins.length > 0 && existingMReveals.length === 0) {
      // No m_reveals from server — synthesize them from newCoins mystery data
      for (const mc of mysteryCoins) {
        const tier = MYSTERY_ID_TO_TIER[mc.symbolId];
        const value = TIER_TO_VALUE[tier] || 15;
        const synthesized = {
          action: "m_reveals",
          coordinates: [mc.reelIndex, mc.symbolIndex],
          tier,
          value,
        };
        screenProgress.push(synthesized);
      }
    }

    // Build Zeus activation queue from newCoins (computed by RGS.js from screen diff)
    // GDD §5.10: Order M → D → C globally, grid order (left-to-right, top-to-bottom) within same type.
    // CRITICAL: Only include Zeus coins that have corresponding feature actions in screen_progress.
    // A Zeus coin that lands with only 'new_coins' (no m_reveals/d_doubles/c_collect) is just a
    // new landing — it will be activated in a future spin when the server sends the feature action.
    const ZEUS_ORDER = ["M", "D", "C"];
    const zeusCoins = newCoins.filter(c => c.isZeus);

    // Pre-compute which feature types have actions in screen_progress
    const actionTypes = new Set(screenProgress.map(a => a.action));
    const featuresWithActions = new Set();
    for (const [feature, actions] of Object.entries(FEATURE_TO_ACTIONS)) {
      if (actions.some(a => actionTypes.has(a))) {
        featuresWithActions.add(feature);
      }
    }

    // Build per-instance entries sorted: M→D→C, then grid order within each type
    const activationQueue = [];
    for (const featureType of ZEUS_ORDER) {
      if (!featuresWithActions.has(featureType)) {
        // No feature actions for this type — skip (they're just new coin landings)
        continue;
      }
      const coinsOfType = zeusCoins
        .filter(c => c.zeusFeature === featureType)
        .sort((a, b) => a.reelIndex !== b.reelIndex ? a.reelIndex - b.reelIndex : a.symbolIndex - b.symbolIndex);
      for (const coin of coinsOfType) {
        activationQueue.push({
          feature: featureType,
          reelIndex: coin.reelIndex,
          symbolIndex: coin.symbolIndex,
        });
      }
    }

    // ── Grid-full GJP popup: show BEFORE features execute ──
    // When 15th coin lands, the glow is already playing. Wait for it,
    // then show the GJP popup. Features (Mystery/Double/Collect) run AFTER dismiss.
    const coinCountNow = store.getState().potCollection?.coinCount || 0;
    const _winLimitReachedNow = !!store.getState().potCollection?.winLimitReached;
    if (coinCountNow >= TOTAL_CELLS && !_winLimitReachedNow) {
      // Wait for reel glow to finish
      if (this._reelGlowPromise) {
        await this._reelGlowPromise;
      }

      // Let the player see the full grid before popup
      await this._delay(500);

      this.dispatch(setShowPotCollectionGrandJackpot(true));
      await new Promise((resolve) => {
        const unsub = store.subscribe(() => {
          if (this._cancelled || !store.getState().animations.showPotCollectionGrandJackpot) {
            unsub();
            resolve();
          }
        });
      });
      // GJP win animation stays playing — stopped when GJP trail arrives at collector
    }

    // ── Feature animation window — set flag so button can route spacebar to slamStop ──
    const hasFeatures = screenProgress.length > 0 || activationQueue.length > 0;
    if (hasFeatures) {
      this._featureAnimating = true;
    }

    if (screenProgress.length > 0 || activationQueue.length > 0) {
      if (!this._screenProgressProcessor) {
        this._screenProgressProcessor = new PotCollectionScreenProgressProcessor({
          overlayReelRefs: this.overlayReelRefs,
          reelRefs: this.reelRefs,
          collectionLayerContainer: this.collectionLayerRef?.current?.getContainer?.(),
          symbolWidth: this.symbolWidth,
          symbolHeight: this.symbolHeight,
          reelSpacing: this.reelSpacing,
          onCoinLanded: () => {
            // Coin counter update already handled by processor
          },
        });

        // Pre-warm the Double feature's pooled 2x label spine now, before any
        // activation fires. This processor is cached across respins, so the
        // one-time Spine.from() cold-start happens here (right after cells stop,
        // while landing animations play) instead of stuttering on the first
        // respin Double activation (iPhone 11).
        this._screenProgressProcessor.prewarm();
      }

      if (activationQueue.length > 0) {
        // Wait for reel glow to finish before activations (grid-full spins)
        if (this._reelGlowPromise) {
          await this._reelGlowPromise;
        }

        // New Zeus coins — run full activation sequence (trail from POT → animate → feature)

        const animator = this.potChargingAnimatorRef?.current;
        const trailContainer = animator?.trailContainer;
        const gameViewSpine = animator?.gameViewSpine;

        if (trailContainer && !trailContainer.destroyed) {
          const orbActivator = new PotCollectionOrbActivator(
            trailContainer,
            gameViewSpine,
            this.symbolWidth,
            this.symbolHeight,
          );

          const potControllers = this.gameViewControllerRef?.current?.potControllers;

          // ── Per-instance screen_progress partitioning ──
          // For each action type, build a consumption cursor so each Zeus instance
          // gets its own share of actions. E.g. 2 m_reveals → first goes to M₁, second to M₂.
          const actionCursors = {};
          for (const action of screenProgress) {
            if (!actionCursors[action.action]) {
              actionCursors[action.action] = { actions: [], cursor: 0 };
            }
            actionCursors[action.action].actions.push(action);
          }

          // Store ref so requestSlamStop() can forward to it
          this._activeOrbActivator = orbActivator;
          // Inherit slam stop if it was requested while cells were still stopping
          if (this._slamStopRequested) {
            orbActivator.requestSlamStop();
          }

          await orbActivator.activateOrbs({
            activationQueue,
            reelSpacing: this.reelSpacing,
            overlayReelRefs: this.overlayReelRefs,
            potControllers,
            skipPositions: this._activatedZeusPositions,
            speedMode: "normal",
            onActivated: ({ feature, reelIndex, symbolIndex }) => {
              // Track position for OrbActivator's skip logic (still needed for visual positioning)
              this._activatedZeusPositions.add(`${reelIndex}_${symbolIndex}`);
            },
            // After each Zeus activate animation, consume that instance's screen_progress actions
            onFeatureReady: async ({ feature, reelIndex, symbolIndex, queueIndex }) => {
              const actionPrefixes = FEATURE_TO_ACTIONS[feature] || [];
              // Consume the NEXT action(s) for each action type belonging to this feature
              const featureActions = [];
              for (const prefix of actionPrefixes) {
                const bucket = actionCursors[prefix];
                if (bucket && bucket.cursor < bucket.actions.length) {
                  featureActions.push(bucket.actions[bucket.cursor]);
                  bucket.cursor++;
                }
              }

              // ── Multi-double context ──
              // For N doubles, the k-th double's intermediate value = finalValue / 2^(N-1-k).
              // The BE only sends the FINAL values grid. The FE must back-compute intermediates.
              let doubleContext = null;
              if (feature === 'D') {
                const dBucket = actionCursors['d_doubles'];
                if (dBucket) {
                  const doubleIndex = dBucket.cursor - 1; // cursor was just incremented
                  const totalDoubles = dBucket.actions.length;
                  doubleContext = { doubleIndex, totalDoubles };
                }
              }

              // ── Pending (not-yet-activated) Zeus positions ──
              // When multiple Zeus of the same type land in one spin, the first one
              // must NOT affect the later ones (they haven't been activated yet).
              // Build a set of positions that are still pending activation.
              const currentIdx = queueIndex !== undefined ? queueIndex : 0;
              const pendingZeusPositions = new Set();
              for (let qi = currentIdx + 1; qi < activationQueue.length; qi++) {
                const pending = activationQueue[qi];
                pendingZeusPositions.add(`${pending.reelIndex}_${pending.symbolIndex}`);
              }

              if (featureActions.length > 0) {
                // Pass reactive slamStop check — animations read this at each step
                const slamStop = this._slamStopRequested;
                await this._screenProgressProcessor.processAll(
                  featureActions,
                  this.dispatch,
                  slamStop,
                  { reelIndex, symbolIndex },
                  doubleContext,
                  this, // spinEngine ref for reactive slamStop checks
                  pendingZeusPositions,
                );

              }

              // Auto speed: reset slam stop so next feature starts at normal speed.
              // User must slam again for each individual feature.
              this._resetSlamStopForNextFeature();
            },
            onAllActivated: () => {
            },
            slamStop: this._slamStopRequested,
          });

          orbActivator.destroy();
          this._activeOrbActivator = null;
        }

        // Process any remaining non-Zeus actions (e.g. new_coins without Zeus)
        const zeusActionPrefixes = Object.values(FEATURE_TO_ACTIONS).flat();
        const remainingActions = screenProgress.filter(a => !zeusActionPrefixes.includes(a.action));
        if (remainingActions.length > 0) {
          await this._screenProgressProcessor.processAll(
            remainingActions,
            this.dispatch,
            this._slamStopRequested,
            null,
            null,
            this,
          );
        }
      } else {
        // No new Zeus — process all screen_progress actions directly (e.g. new_coins only)
        await this._screenProgressProcessor.processAll(
          screenProgress,
          this.dispatch,
          this._slamStopRequested,
          null,
          null,
          this,
        );
      }
    }

    this._featureAnimating = false;
    this._slamStopRequested = false;
    this.dispatch(setFeatureSpinComplete({ complete: true, bet }));

    // ── Sync Redux with API's respinsLeft (always, not just on coin landing) ──
    // The visual counter animation is imperative (component-level).
    // Redux must always reflect the server's truth so the button and end-of-bonus
    // checks read the correct value.
    this.dispatch(applyPotCollectionPendingRespins());

    // Check if bonus should end
    const updatedState = store.getState();
    const respinsLeft = updatedState.potCollection?.respinsLeft || 0;
    const coinCount = updatedState.potCollection?.coinCount || 0;

    // Determine if features were activated on this final spin
    // (used to skip settling pause — feature animations provide natural delay)
    const hadFeatures = screenProgress && screenProgress.length > 0;

    if (coinCount >= TOTAL_CELLS) {

      // GJP anticipation was already transitioned to win state in _handleSymbolStopComplete.
      // Do NOT stop it here — let it play through until the GJP popup is dismissed.
      // _startEndOfBonusSequence will call _stopGjpAnticipation() after the popup closes.
      if (!this._endOfBonusActive) this._startEndOfBonusSequence(true, hadFeatures);
    } else if (respinsLeft <= 0) {

      // Note: winLimitReached does NOT stop respins early (GDD §18.2).
      // The 10,000x cap is enforced during collection — the counter stops there
      // and the max_win popup replaces the total_win popup. Respins run to completion.
      this._stopGjpAnticipation();
      if (!this._endOfBonusActive) this._startEndOfBonusSequence(false, hadFeatures);
    } else {
      // GJP anticipation is now handled by symbol tension (starts/stops together)

      // More respins — signal spin complete for autoplay
      this.onSpinComplete?.({ coinLanded: this._coinLandedThisSpin });
    }
  }

  // ── Utility ─────────────────────────────────────────────────────────────────

  get isSpinning() {
    return this._isSpinning;
  }

  _clearTimeouts() {
    this._stopTimeouts.forEach((t) => {
      if (t && typeof t.clear === "function") {
        t.clear();
      }
    });
    this._stopTimeouts.clear();
    if (this._autoStopTimeout) {
      this._autoStopTimeout.clear();
      this._autoStopTimeout = null;
    }
    // Clear connection timeout (TickerTimeout — freezes with tab hide)
    this._clearConnectionTimeout();
    // Clear stale tension timeout reference so _triggerCascadingStops
    // doesn't skip tension-start scheduling on the next spin.
    if (this._tensionStopTimeout && typeof this._tensionStopTimeout.clear === "function") {
      this._tensionStopTimeout.clear();
    }
    this._tensionStopTimeout = null;
  }

  /**
   * Clear the connection timeout (API responded or failed before it fired).
   */
  _clearConnectionTimeout() {
    if (this._connectionTimeout) {
      if (typeof this._connectionTimeout.clear === "function") {
        this._connectionTimeout.clear();
      }
      this._connectionTimeout = null;
    }
  }

  /**
   * Dismiss the connection popup if it's currently shown.
   */
  _dismissConnectionPopup() {
    const uiState = store.getState();
    if (uiState.userInterface.connectionPopupActive) {
      this.dispatch(closeConnectionPopup());
    }
  }

  /**
   * Freeze all spinning cells on API failure.
   * Stops tickers, hides spinning containers, resets engine state.
   * Ported from ReelControlContext.freezeReels() pattern.
   */
  _freezeAllSpinningCells() {
    const blankCells = this._currentSpinBlankCells || [];

    // Freeze each spinning cell's controller instantly (mirrors freezeReels → freezeSpin)
    blankCells.forEach(({ symbolRef }) => {
      const controller = symbolRef?.current?.getSymbolController?.();
      if (controller && !controller.hasStopped) {
        controller.freezeSpin();
      }
    });

    // Cancel tension if active
    if (this._lastCoinTensionActive) {
      this._cancelTension();
    }

    // Stop sounds
    soundManager.stop(SOUND_NAME.feature_reel_spin);

    // Cancel all pending timeouts (wind-up cascade, auto-stop, etc.)
    this._clearTimeouts();

    // Dismiss popup if shown
    this._dismissConnectionPopup();

    // Clean up subscriptions
    if (this._failFastUnsubscribe) {
      this._failFastUnsubscribe();
      this._failFastUnsubscribe = null;
    }

    // Reset engine state
    this._isSpinning = false;
    this._currentSpinBlankCells = null;

    // Mark reels as frozen so WarningPopup triggers ErrorRecoveryOverlay on dismiss
    this.dispatch(setReelsFrozen(true));
    this.dispatch(setFeatureSpinComplete({ complete: true, bet: store.getState().game.bet }));
  }

  resize(symbolWidth, symbolHeight, reelSpacing) {
    this.symbolWidth = symbolWidth;
    this.symbolHeight = symbolHeight;
    this.reelSpacing = reelSpacing;
    if (this._screenProgressProcessor) {
      this._screenProgressProcessor.resize(symbolWidth, symbolHeight, reelSpacing);
    }
    if (this._endOfBonusEngine) {
      this._endOfBonusEngine.resize(symbolWidth, symbolHeight, reelSpacing);
    }
    if (this._activeOrbActivator) {
      this._activeOrbActivator.resize(symbolWidth, symbolHeight);
    }
  }

  cancel() {
    this._cancelled = true;
    this._slamStopRequested = false;
    this._featureAnimating = false;
    this._clearTimeouts();
    // Dismiss connection popup if it was already opened before cancel
    this._dismissConnectionPopup();
    if (this._manualStopUnsubscribe) {
      this._manualStopUnsubscribe();
      this._manualStopUnsubscribe = null;
    }
    // Clean up API wait subscription (prevents duplicate-fire on next spin)
    if (this._apiWaitUnsubscribe) {
      this._apiWaitUnsubscribe();
      this._apiWaitUnsubscribe = null;
    }
    // Clean up fast-path failure subscription
    if (this._failFastUnsubscribe) {
      this._failFastUnsubscribe();
      this._failFastUnsubscribe = null;
    }
    this._currentSpinBlankCells = null;
    if (this._screenProgressProcessor) {
      this._screenProgressProcessor.cancel();
    }
    if (this._activeOrbActivator) {
      this._activeOrbActivator.cancel();
      this._activeOrbActivator = null;
    }
    if (this._endOfBonusEngine) {
      this._endOfBonusEngine.cancel();
      this._endOfBonusEngine = null;
    }
    this._endOfBonusActive = false;

    // Clean up GJP overlays (may still be parented to game_view)
    this._stopGjpAnticipation();
    this._stopGjpWin();

    // Direct fallback: force-destroy GJP anticipation overlay if stop call couldn't
    // complete (e.g., already mid-loop_out when cancel was called)
    if (this._gjpOverlaySpine) {
      this._destroyOverlayDirect(this._gjpOverlaySpine);
      this._gjpOverlaySpine = null;
    }

    // Clean up tension API subscription
    if (this._tensionApiUnsub) {
      this._tensionApiUnsub();
      this._tensionApiUnsub = null;
    }

    // Clean up any pending tension state
    this._pendingTensionCleanup = false;
    this._pendingTensionCell = null;
    this._lastCoinTensionActive = false;
    this._tensionCell = null;
  }

  destroy() {
    this.cancel();
    this._destroyCoinCollector();
    if (this._screenProgressProcessor) {
      this._screenProgressProcessor.destroy();
      this._screenProgressProcessor = null;
    }
    this.reelRefs = null;
    this.overlayReelRefs = null;
    this.collectionLayerRef = null;
    this.dispatch = null;
  }

  /**
   * Destroy the coin_collector spine and remove from parent.
   * Called after 'out' animation completes, and as safety in destroy().
   */
  _destroyCoinCollector() {
    if (this._coinCollectorSpine) {
      if (!this._coinCollectorSpine.destroyed) {
        this._coinCollectorSpine.state?.clearListeners();
        this._coinCollectorSpine.state?.clearTracks();
        if (this._coinCollectorSpine.parent) {
          this._coinCollectorSpine.parent.removeChild(this._coinCollectorSpine);
        }
        this._coinCollectorSpine.destroy();
      }
      this._coinCollectorSpine = null;
    }
  }

  // ── GJP Anticipation (GDD §14.3) ──

  /**
   * Destroy an overlay spine directly without going through GVC.
   * Handles removeChild, destroy, and _activeOverlays cleanup.
   * Safe to call if the overlay is already destroyed or detached.
   */
  _destroyOverlayDirect(overlay) {
    if (!overlay) return;
    try {
      overlay.state?.clearListeners();
      overlay.state?.clearTracks();
    } catch (_) {}
    // Remove from GVC _activeOverlays if GVC is still alive
    const gvc = this.gameViewControllerRef?.current;
    gvc?._activeOverlays?.delete(overlay);
    if (!overlay.destroyed) {
      if (overlay.parent) overlay.parent.removeChild(overlay);
      overlay.destroy();
    }
  }

  // ── End-of-Bonus Sequence ──────────────────────────────────────────────────

  /**
   * Start the end-of-bonus sequence (GDD §13.1 steps 18–24).
   * Step 1: Wait 300ms, then play reel frame glow.
   * Subsequent steps (counter transition, value collection, etc.) will be added.
   *
   * @param {boolean} isGridFull - Whether the grid is completely filled (15/15)
   * @param {boolean} hadFeatures - Whether features were activated on the final spin
   */
  async _startEndOfBonusSequence(isGridFull, hadFeatures = false) {
    if (this._endOfBonusActive) {

      return;
    }
    this._endOfBonusActive = true;

    // Bonus End animation fires earlier (in _handleSymbolStopComplete, at cell stop time).
    // Glow is independent — already fired on 15th coin land (fire-and-forget).
    // No need to await or trigger either here.

    // ── Settling pause ──
    // Always pause after last cell stops — glow or bonus_end animation plays during this time.
    if (!hadFeatures) {
      await this._delay(1000);
    }

    // Grid-full GJP popup already handled in _handleAllCellsStopped.
    // Only handle max-win cleanup here.
    const _winLimitReached = !!store.getState().potCollection?.winLimitReached;
    if (isGridFull && _winLimitReached) {
      // Max win path: clean up any lingering GJP overlays
      this._stopGjpAnticipation();
      this._stopGjpWin();
    }

    // ── Counter out → Collector in (GDD §14.2 step 8) ──
    // After GJP popup dismissed (or immediately if not grid-full),
    // transition respin counter into win collector.
    const counterCtrl = this.respinCounterRef?.current;
    if (counterCtrl) {
      await counterCtrl.playOut();
    }

    // Show coin collector (in → idle) — resolves when idle starts
    await this._showCoinCollector();

    // Step 4-6: Value collection → Grand Jackpot → Total Win
    // The EndOfBonusEngine handles: trail from each coin → collector, hit anims,
    // counter text, flip-reveals, darken+swap, GJP, max win, total win popup.
    this._endOfBonusEngine = new PotCollectionEndOfBonusEngine({
      overlayReelRefs: this.overlayReelRefs,
      reelRefs: this.reelRefs,
      collectionLayerRef: this.collectionLayerRef,
      coinCollectorSpine: this._coinCollectorSpine,
      dispatch: this.dispatch,
      onCollectionComplete: (totalWin) => {
      },
      symbolWidth: this.symbolWidth,
      symbolHeight: this.symbolHeight,
      reelSpacing: this.reelSpacing,
    });

    // Collection is a "feature animation" — keeps button active for slam stop
    this._featureAnimating = true;
    await this._endOfBonusEngine.runCollection(this._slamStopRequested);
    this._featureAnimating = false;

    // GJP: if grid full (15/15), play Grand Jackpot animation + trail value to collector
    // GDD §18.2: if the cap was already hit during coin collection, skip GJP popup AND trail
    const bet = store.getState().game.bet || 1;
    const maxWinMultiplier = FEATURES_CONFIG.POT_COLLECTION?.MAX_WIN_MULTIPLIER || 10000;
    const maxWin = maxWinMultiplier * bet;
    const capAlreadyHit = this._endOfBonusEngine?._collectedTotal >= maxWin;

    if (isGridFull && !capAlreadyHit) {
      const gvc = this.gameViewControllerRef?.current;
      const eob = this._endOfBonusEngine;
      const gjpDisplayValue = GRAND_JACKPOT_VALUE * bet; // display value in cents (5000 × bet)

      const gjpCapped = Math.min(gjpDisplayValue, maxWin - (eob?._collectedTotal || 0));

      // Get GJP label global position → convert to collection layer local
      let gjpFromPos = null;
      if (gvc && eob) {
        const globalPos = gvc.getGrandJackpotLabelPosition();
        const collectionContainer = eob._getCollectionContainer();
        if (globalPos && collectionContainer && !collectionContainer.destroyed) {
          gjpFromPos = collectionContainer.toLocal(globalPos);
        }
      }

      // Track when hit_final animation completes (set inside onArrival)
      let hitFinalComplete = null;

      // Single-leg trail: GJP label → collector via grid center (cubic Bézier)
      // Scale profile: 1× → 4× at center → counterScale at collector
      if (eob && eob._valueTraveler && gjpFromPos) {
        const { formatToCustomLocale } = require('../../utilities');
        const { FONT_STYLES, scaleStyle } = require('../../fontStyles');

        const centerPos = eob._getGridPosition(2, 1); // center of 5×3 grid
        const toPos = eob._getCollectorTargetPosition();
        const prevTotal = eob._collectedTotal;
        eob._collectedTotal += gjpCapped;
        const newTotal = eob._collectedTotal;

        const displayText = formatToCustomLocale(gjpCapped);
        // Use coinValue style (same as all coin trails and collector)
        const fontSize = this.symbolHeight * Settings.COIN_VALUE_FONT_SIZE_MULTIPLIER;
        const textStyle = new PIXI.TextStyle(scaleStyle(FONT_STYLES.coinValue, fontSize));

        // Compute scale ratio: counter height vs trail text height
        let scaleTo = 0.5;
        const collContainer = eob._getCollectionContainer();
        if (eob._counterText && !eob._counterText.destroyed && collContainer) {
          try {
            const halfH = eob._counterText.height / 2;
            const gTop = eob._counterText.toGlobal({ x: 0, y: -halfH });
            const gBot = eob._counterText.toGlobal({ x: 0, y: halfH });
            const globalCounterH = Math.abs(gBot.y - gTop.y);
            const tTop = collContainer.toGlobal({ x: 0, y: -fontSize / 2 });
            const tBot = collContainer.toGlobal({ x: 0, y: fontSize / 2 });
            const globalTrailH = Math.abs(tBot.y - tTop.y);
            if (globalTrailH > 0) scaleTo = globalCounterH / globalTrailH;
          } catch (e) { /* fallback stays 0.5 */ }
        }

        // Two-leg trail: GJP label → center (arc + hold) → collector (arc)
        // Same arc shape as regular coin/collect trails for visual consistency
        soundManager.play(SOUND_NAME.jackpot_collect);
        await eob._valueTraveler.travelTwoLeg({
          text: displayText,
          style: textStyle,
          from: gjpFromPos,
          to: toPos,
          via: centerPos,
          leg1Duration: 500,
          holdDuration: 400,
          leg2Duration: 500,
          scaleFrom: 1,
          scalePeak: 3,
          scaleTo,
          onArrival: () => {
            // Show counter if hidden
            if (eob._counterText && eob._counterText.alpha === 0) {
              eob._counterText.alpha = 1;
            }
            // Play hit_final on collector (reserved for Grand Jackpot only)
            const collectorSpine = this._coinCollectorSpine;
            if (collectorSpine?.state && !collectorSpine.destroyed) {
              collectorSpine.state.setAnimation(0, 'hit_final', false);
              collectorSpine.state.addAnimation(0, 'idle', true, 0);

              // Track when hit_final finishes so we can wait before playing 'out'
              hitFinalComplete = new Promise((resolve) => {
                const safetyTid = new TickerTimeout(resolve, 5000);
                this._stopTimeouts.set('hit-final-safety', safetyTid);
                const listener = {
                  complete: (entry) => {
                    if (entry.animation.name === 'hit_final') {
                      safetyTid.clear();
                      this._stopTimeouts.delete('hit-final-safety');
                      collectorSpine.state.removeListener(listener);
                      resolve();
                    }
                  },
                };
                collectorSpine.state.addListener(listener);
              });
            }
            // GJP value arrived at collector — NOW stop the win animation
            this._stopGjpWin();
          },
        });
        // GJP counter phase — fresh loop + counter_end when tick finishes
        await eob._animateCounter(prevTotal, newTotal, true);
      }
      // Wait for hit_final animation to finish naturally before playing out
      if (hitFinalComplete) {
        await hitFinalComplete;
      }

    }

    // Brief pause in idle before dismissing
    await this._delay(1000);

    // Play out on collector (always, regardless of GJP)
    if (this._coinCollectorSpine?.state && !this._coinCollectorSpine.destroyed) {
      await new Promise((resolve) => {
        this._coinCollectorSpine.state.setAnimation(0, 'out', false);
        let safetyTid = null;
        const done = () => {
          if (safetyTid) { safetyTid.clear(); this._stopTimeouts.delete('collector-out-safety'); }
          this._coinCollectorSpine?.state?.removeListener(listener);
          resolve();
        };
        const listener = { complete: done };
        this._coinCollectorSpine.state.addListener(listener);
        safetyTid = new TickerTimeout(done, 3000);
        this._stopTimeouts.set('collector-out-safety', safetyTid);
      });

      // Clean up spine after out animation
      this._destroyCoinCollector();
    }

    // Destroy EOB engine
    if (this._endOfBonusEngine) {
      this._endOfBonusEngine.destroy();
      this._endOfBonusEngine = null;
    }

    // Show Max Win popup instead of Total Win when the 10,000x cap was hit (GDD §18.2).
    // winLimitReached is set by the API and stored in potCollectionSlice.
    // capAlreadyHit is the FE-side guard (collection counter hit MAX_WIN_MULTIPLIER × bet).
    // In production both agree; for testing we can lower the FE multiplier independently.
    const winLimitReached = !!store.getState().potCollection?.winLimitReached;
    if (winLimitReached || capAlreadyHit) {
      this.dispatch(setShowPotCollectionMaxWin(true));
    } else {
      this.dispatch(setShowPotCollectionOutro(true));
    }
  }

  /**
   * Play the Bonus_bg_reel_activation glow as a separate spine overlay (GDD §22.5).
   * Delegates to GameViewController.playReelGlow() which creates a temporary
   * spine instance to avoid breaking the main game_view spine's idle.
   */
  _playReelGlow() {
    const gvc = this.gameViewControllerRef?.current;
    if (!gvc?.playReelGlow) {

      return Promise.resolve();
    }

    return gvc.playReelGlow();
  }

  /**
   * Play the bonus_end animation as a separate spine overlay.
   * Delegates to GameViewController.playBonusEnd().
   * Used at end-of-bonus when grid is NOT full (no Grand Jackpot).
   */
  _playBonusEnd() {
    const gvc = this.gameViewControllerRef?.current;
    if (!gvc?.playBonusEnd) {

      return Promise.resolve();
    }

    return gvc.playBonusEnd();
  }

  _delay(ms) {
    if (this._cancelled) return Promise.resolve();
    return new Promise((resolve) => {
      const tid = new TickerTimeout(() => {
        this._stopTimeouts.delete(`delay-${Date.now()}`);
        resolve();
      }, ms);
      this._stopTimeouts.set(`delay-${Date.now()}`, tid);
    });
  }

  /**
   * Create the coin_collector spine at the attachment_collect_pot bone,
   * play 'in' animation, then chain to 'idle' loop.
   * @returns {Promise} Resolves when 'in' animation completes and idle starts.
   */
  _showCoinCollector() {
    const gvc = this.gameViewControllerRef?.current;
    if (!gvc) {

      return Promise.resolve();
    }

    const skeletonAlias = 'coin_collector-skeleton';
    const atlasAlias = 'coin_collector-atlas';
    if (!PIXI.Assets.cache.has(skeletonAlias) || !PIXI.Assets.cache.has(atlasAlias)) {

      return Promise.resolve();
    }

    // Use the same container as the respin counter (already bone-synced to
    // txt_slot_attachment_respin_counter and positioned correctly every frame).
    const parentContainer = gvc._respinCounterContainer;
    if (!parentContainer) {

      return Promise.resolve();
    }

    // Create spine
    const collectorSpine = Spine.from({ skeleton: skeletonAlias, atlas: atlasAlias });
    this._coinCollectorSpine = collectorSpine;

    // Scale collector to be bigger than respin counter
    // (native 390×83 vs respin counter 534×181; at 1.5× → 585×124)
    const collectorScale = 1.5;
    collectorSpine.scale.set(collectorScale);

    // Same Y offset as respin counter (-38) for consistent vertical positioning
    collectorSpine.position.set(0, -38);
    parentContainer.addChild(collectorSpine);

    return new Promise((resolve) => {
      try {
        soundManager.play(SOUND_NAME.collector_in);
        collectorSpine.state.setAnimation(0, 'in', false);
        const listener = {
          complete: (entry) => {
            if (entry.animation.name === 'in') {
              collectorSpine.state.removeListener(listener);
              // Chain to idle loop
              collectorSpine.state.setAnimation(0, 'idle', true);
              resolve();
            }
          },
        };
        collectorSpine.state.addListener(listener);
      } catch (err) {

        resolve();
      }
    });
  }

  /**
   * Start 15th coin anticipation as a separate spine overlay.
   * Creates a temporary game_view spine instance so the animation
   * does not break the main spine's idle (same pattern as reel glow).
   */
  _startGjpAnticipation() {
    this._gjpAnticipationActive = true;
    const gvc = this.gameViewControllerRef?.current;
    if (!gvc) return;

    // --- Anticipation overlay (loop_in → loop) ---
    const anticipationOverlay = gvc._createAnticipationOverlay?.();
    if (!anticipationOverlay) return;
    this._gjpOverlaySpine = anticipationOverlay;

    const introAnim = anticipationOverlay.skeleton.data.findAnimation("gjp_anticipation_loop_in");
    if (introAnim) {
      anticipationOverlay.state.setAnimation(0, "gjp_anticipation_loop_in", false);
      anticipationOverlay.state.addListener({
        complete: (entry) => {
          if (entry.animation.name === "gjp_anticipation_loop_in" && this._gjpAnticipationActive) {
            anticipationOverlay.state.setAnimation(0, "gjp_anticipation_loop", true);
          }
        },
      });
    }

    // Win overlay is independent — created in _playGjpAnticipationWin() when 15th coin lands.
  }

  /**
   * Stop the GJP anticipation overlay.
   * Plays loop_out → destroys.
   */
  _stopGjpAnticipation() {
    if (!this._gjpAnticipationActive) return;
    this._gjpAnticipationActive = false;

    const anticipationOverlay = this._gjpOverlaySpine;
    if (!anticipationOverlay) return;
    // Null ref immediately so concurrent calls/cancel can't double-destroy
    this._gjpOverlaySpine = null;

    const outAnim = anticipationOverlay.skeleton?.data?.findAnimation('gjp_anticipation_loop_out');
    if (outAnim) {
      anticipationOverlay.state.clearListeners();
      anticipationOverlay.state.setAnimation(0, 'gjp_anticipation_loop_out', false);
      anticipationOverlay.state.addListener({
        complete: () => {
          // Self-cleanup — don't rely on GVC which may be destroyed by now
          this._destroyOverlayDirect(anticipationOverlay);
        },
      });
    } else {
      this._destroyOverlayDirect(anticipationOverlay);
    }
  }

  /**
   * Play the GJP win overlay — fully independent from anticipation.
   * Creates its own spine overlay, plays win_loop_in → win_loop.
   * Called when the 15th coin lands (grid full), alongside reel glow.
   * Loops until _stopGjpWin() is called (when GJP popup is dismissed).
   */
  _playGjpAnticipationWin() {
    const gvc = this.gameViewControllerRef?.current;
    if (!gvc) return;

    this._gjpAnticipationWin = true;
    gvc.playGjpWin();
  }

  /**
   * Stop the GJP win overlay.
   * Plays win_loop_out → destroys. Called when GJP popup is dismissed.
   */
  _stopGjpWin() {
    this._gjpAnticipationWin = false;

    const gvc = this.gameViewControllerRef?.current;
    if (gvc) {
      gvc.stopGjpWin();
    }
  }

  // ── Last-Coin Tension ─────────────────────────────────────────────────────

  /**
   * Pre-calculate whether tension should activate this spin.
   *
   * Spec: anticipation ONLY when exactly 1 empty cell remains on the grid
   * (sole blank spinning this spin), with 14 coins already confirmed.
   * Multi-blank spins (e.g. 3 empties) must NEVER start anticipation on the
   * last cascade cell — that was the QA false-trigger / stuck-skip bug.
   *
   * @param {Array} blankCells - The blank cells that were spinning this spin
   * @returns {Object|null} The tension cell ({ reelIndex, symbolIndex, symbolRef }) or null
   */
  _computeTensionCell(blankCells) {
    if (!blankCells || blankCells.length !== 1) {
      return null;
    }

    const state = store.getState();
    const coinCountNow = state.potCollection?.coinCount || 0;

    // Sole blank ⇒ grid should already hold TOTAL_CELLS - 1 coins (14).
    // Guard against desync / stale counts starting anticipation early.
    if (coinCountNow < TOTAL_CELLS - 1) {
      return null;
    }

    // API must have answered (landing data available) before we commit
    const currentReels = state.potCollection?.currentRound?.reels;
    if (!currentReels) {
      return null;
    }

    return blankCells[0];
  }

  /**
   * Start tension on the last blank cell:
   * 1. Play GJP anticipation overlay (game_view spine)
   * 2. Play symbol_tension spine on the overlay symbol (separate tension layer)
   * 3. Decelerate the cell's spin via controller (spinTension)
   * 4. Schedule the tension stop after DURATION
   */
  _startSymbolTension(cell) {
    const stConfig = FEATURES_CONFIG.POT_COLLECTION.SYMBOL_TENSION;
    const duration = stConfig?.DURATION ?? 3500;

    // 1. Start GJP anticipation overlay
    if (!this._gjpAnticipationActive) {
      this._startGjpAnticipation();
    }

    // 1b. Background anticipation (storm) + bone-level tension shake
    //     Start both immediately so they sync with symbol tension spine
    const gvc = this.gameViewControllerRef?.current;
    if (gvc) {
      gvc.playAnticipation(); // storm intro → loop (no callback needed)
      gvc.startTensionEffect(this.symbolHeight); // shake starts immediately
    }

    // 2. Play symbol_tension spine on the overlay symbol + start tension sound
    const overlayReelRef = this.overlayReelRefs?.current?.[cell.reelIndex];
    const overlaySymbolRef = overlayReelRef?.getSymbolRef?.(cell.symbolIndex);
    if (overlaySymbolRef?.playTensionIn) {
      overlaySymbolRef.playTensionIn();
      soundManager.play(SOUND_NAME.main_anticipation_loop);
    } else {

    }

    // 3. Decelerate the cell's spin via the symbol controller
    if (cell.symbolRef?.current?.spinTension) {
      cell.symbolRef.current.spinTension();
    }

    // 4. Schedule the tension stop
    this._tensionStopTimeout = new TickerTimeout(() => {
      if (this._cancelled || !this._lastCoinTensionActive) return;
      this._stopSymbolTension();
    }, duration);
    this._stopTimeouts.set('tension-stop', this._tensionStopTimeout);
  }

  /**
   * Stop tension: play out animations and stop the cell.
   */
  _stopSymbolTension() {
    if (!this._lastCoinTensionActive) return;

    const cell = this._tensionCell;
    const controller = cell?.symbolRef?.current?.getSymbolController?.();

    // Guard: if landing data hasn't arrived (slow network), defer stop until API responds.
    // The controller needs pendingLandingSymbolId to execute the stop — without it,
    // the spin loop waits forever for a wrap+stop that never comes.
    if (controller && controller.pendingLandingSymbolId === null) {

      // Subscribe to store and retry when API data arrives
      const unsub = store.subscribe(() => {
        const state = store.getState();
        if (state.spin.featureSpinStatus === 'succeeded') {
          unsub();
          this._tensionApiUnsub = null;
          // Landing data should be set by _handleApiSuccess by now
          if (this._lastCoinTensionActive) {
            this._stopSymbolTension();
          }
        } else if (state.spin.featureSpinStatus === 'failed') {
          unsub();
          this._tensionApiUnsub = null;
          this._cancelTension();
        }
      });
      this._tensionApiUnsub = unsub;
      return;
    }

    // 1. Register onLandingPeak on the symbol controller — this fires at the
    //    BackOut overshoot trough (lowest visual point, before bounce-back),
    //    giving us a "nudge before settle" moment to start cleaning up effects.
    //    Matches base game's ReelControllerV2 onLandingPeak pattern.
    if (controller) {
      controller.onLandingPeak = () => {
        controller.onLandingPeak = null; // One-shot
        if (this._pendingTensionCleanup) {
          this._cleanupTensionAtmosphere();
        }
      };
    }

    // 2. Stop the cell's spin via controller (tension-aware stop)
    //    This starts the BackOut bounce. ALL tension visuals (lightning spine,
    //    storm, GJP anticipation) stay visible during the bounce and are
    //    cleaned up at the overshoot trough via onLandingPeak.
    if (cell?.symbolRef?.current?.stopSpinTension) {
      cell.symbolRef.current.stopSpinTension();
    } else if (cell?.symbolRef?.current?.stopSpin) {
      cell.symbolRef.current.stopSpin();
    }

    // 3. Mark cleanup pending + save cell position for deferred playTensionOut
    this._pendingTensionCleanup = true;
    this._pendingTensionCell = cell;

    // 4. Clear tension state
    this._lastCoinTensionActive = false;
    this._tensionCell = null;
    if (this._tensionStopTimeout) {
      this._tensionStopTimeout.clear();
      this._tensionStopTimeout = null;
    }
    this._stopTimeouts.delete('tension-stop');
    this._stopTimeouts.delete('tension-start');
  }

  /**
   * Clean up atmospheric effects (GJP anticipation, storm, tension zoom).
   * Called from _handleSymbolStopComplete when the symbol finishes its
   * BackOut bounce — NOT from _stopSymbolTension, to keep effects visible
   * during the landing animation (matching base game behavior).
   *
   * @param {boolean} skipGjp - If true, skip stopping the GJP anticipation overlay.
   *   Used when the 15th coin lands so the overlay transitions to win state instead.
   */
  _cleanupTensionAtmosphere(skipGjp = false) {
    this._pendingTensionCleanup = false;

    // Play tension lightning spine "out" on the overlay — deferred from
    // _stopSymbolTension so the lightning stays visible during the bounce.
    const cell = this._pendingTensionCell;
    if (cell) {
      const overlayReelRef = this.overlayReelRefs?.current?.[cell.reelIndex];
      const overlaySymbolRef = overlayReelRef?.getSymbolRef?.(cell.symbolIndex);
      if (overlaySymbolRef?.playTensionOut) {
        overlaySymbolRef.playTensionOut();
      }
      this._pendingTensionCell = null;
    }

    // Stop symbol tension sound
    soundManager.stop(SOUND_NAME.main_anticipation_loop);

    // Stop GJP anticipation glow — unless we're transitioning it to win state
    if (!skipGjp) {
      this._stopGjpAnticipation();
    }

    // Stop background anticipation (storm) + bone-level shake
    const gvc = this.gameViewControllerRef?.current;
    if (gvc) {
      gvc.stopAnticipation();
      gvc.stopTensionEffect();
    }
  }

  /**
   * Cancel tension immediately without playing out animations.
   * Used by manual stop / slam stop / spin reset.
   *
   * IMPORTANT: No early return on _lastCoinTensionActive — GVC effects must
   * ALWAYS be cleaned up. _stopSymbolTension() sets _lastCoinTensionActive=false
   * but defers GVC cleanup to _handleSymbolStopComplete. If a new spin starts
   * before that callback fires, this method must still kill the GVC shake/storm.
   */
  _cancelTension() {
    // Clear timeout
    if (this._tensionStopTimeout) {
      this._tensionStopTimeout.clear();
      this._tensionStopTimeout = null;
    }
    this._stopTimeouts.delete('tension-stop');
    this._stopTimeouts.delete('tension-start');

    // Hide symbol tension spine on the overlay (immediate, no out animation).
    // After _stopSymbolTension(), the cell lives on _pendingTensionCell while
    // _tensionCell is null — must clear BOTH or the lightning stays stuck
    // (QA: anticipation stuck on last reel / last row after skip).
    const cellsToHide = [this._tensionCell, this._pendingTensionCell].filter(Boolean);
    for (const cell of cellsToHide) {
      const overlayReelRef = this.overlayReelRefs?.current?.[cell.reelIndex];
      const overlaySymbolRef = overlayReelRef?.getSymbolRef?.(cell.symbolIndex);
      if (overlaySymbolRef?.hideTension) {
        overlaySymbolRef.hideTension();
      }
    }

    // Stop symbol tension sound
    soundManager.stop(SOUND_NAME.main_anticipation_loop);

    // Stop GJP anticipation glow in sync with symbol tension
    this._stopGjpAnticipation();

    // Stop background anticipation (storm) + bone-level shake (immediate kill)
    // Only when tension was actually started — otherwise stopAnticipation plays
    // anticipation_out (cloud movement) on every spin reset even without tension.
    const hadTension = this._lastCoinTensionActive || this._tensionFiredThisSpin || this._pendingTensionCleanup;
    const gvc = this.gameViewControllerRef?.current;
    if (gvc && hadTension) {
      gvc.stopAnticipation();
      gvc._killTensionEffect?.();
    }

    // Reset deceleration on the tension cell's controller
    const tensionForController = this._tensionCell || this._pendingTensionCell;
    if (tensionForController?.symbolRef?.current?.getSymbolController) {
      const controller = tensionForController.symbolRef.current.getSymbolController();
      if (controller) {
        controller._resetTensionState?.();
      }
    }

    // Stop the tension cell if it's still spinning
    if (tensionForController?.symbolRef?.current?.stopSpin) {
      const controller = tensionForController.symbolRef.current.getSymbolController?.();
      if (controller && !controller.hasStopped && controller.isPlaying) {
        tensionForController.symbolRef.current.stopSpin();
      }
    }

    this._pendingTensionCleanup = false;
    this._pendingTensionCell = null;
    this._lastCoinTensionActive = false;
    this._tensionCell = null;
    this._preCachedTensionCell = null;
  }
}

export default PotCollectionSpinEngine;
